import { useEffect, useRef, type ReactNode } from 'react';
import { createRootRoute, Outlet, useNavigate, useLocation, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@cortex/web-design-system/components/layout/AppShell';
import type { SidebarRenderNavLinkArgs } from '@cortex/web-design-system/components/layout/Sidebar';
import { Logo } from '@cortex/web-design-system/components/ui/Logo';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';
import { LoadingScreen } from '../components/feedback/LoadingScreen/index.js';
import { useNavGroups } from '../components/layout/useNavGroups.js';
import { HeaderActions } from '../components/layout/HeaderActions.js';
import { UserMenu } from '../components/layout/UserMenu.js';
import { isBareLayoutPath } from '../lib/bare-layout.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ルートレイアウト。認証チェックと共有 AppShell（Header + Sidebar + main）を統合し、全ページの共通レイアウトを提供
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects AppShell [calls] 共有レイアウトシェル呼び出し
 * @graph-connects LoadingScreen [calls] ローディング画面コンポーネント呼び出し
 */
export const Route = createRootRoute({
  component: RootLayout,
});

/**
 * 共有 Sidebar のリンク描画関数。TanStack Router の Link でナビ項目を描画する。
 * `folder` 項目はページ遷移を持たない（子の展開/折り畳みのみ）ため、Link ではなく
 * button で描画して遷移を発生させない。
 * @graph-connects none
 */
function renderNavLink({
  item,
  className,
  children,
  onNavigate,
  ariaExpanded,
}: SidebarRenderNavLinkArgs): ReactNode {
  if (item.folder === true) {
    return (
      <button
        type="button"
        className={`${className} cursor-pointer`}
        onClick={onNavigate}
        aria-expanded={ariaExpanded}
      >
        {children}
      </button>
    );
  }
  return (
    <Link to={item.path} className={className} onClick={onNavigate} aria-expanded={ariaExpanded}>
      {children}
    </Link>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 認証済みユーザー向けの AppShell 描画。ナビゲーション・ヘッダーアクション・ユーザーメニューを組み立てる
 * @graph-connects useNavGroups [calls] サイドバーナビゲーション定義を取得
 */
function AuthenticatedShell(): ReactNode {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const navGroups = useNavGroups();

  return (
    <AppShell
      appName={t('app.name')}
      logo={
        <Link to="/" className="flex items-center gap-2">
          <Logo size="sm" />
        </Link>
      }
      navGroups={navGroups}
      renderNavLink={renderNavLink}
      currentPath={location.pathname}
      headerActions={<HeaderActions />}
      userMenu={user ? <UserMenu user={user} onLogout={logout} /> : undefined}
      sidebarFooter={
        <p className="text-xs text-[var(--color-text-muted)] text-center">
          Product Innovation Division
        </p>
      }
    >
      <Outlet />
    </AppShell>
  );
}

/** @graph-connects isBareLayoutPath [calls] 図の全画面表示など AppShell で包まないパスの判定 */
function RootLayout(): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // テーマ初期化（data-theme属性をhtmlに適用）
  useTheme();

  const isLoginPage = location.pathname === '/login';

  // 開発時のみ: `VITE_MOCK_AUTH=1` で認証ゲートを丸ごとスキップし、モックデータで UI を
  // 確認できるようにする（ops スキャフォールド等）。`import.meta.env.DEV` は本番ビルドで
  // false に畳まれ、この分岐は tree-shake で消えるため本番バンドルには絶対に載らない。
  const devMockAuth =
    import.meta.env.DEV && (import.meta.env.VITE_MOCK_AUTH as string | undefined) === '1';

  // location.pathname の最新値を ref で保持する。
  // deps に含めると route transition 中の複数 render でリダイレクトが多重発火し
  // TanStack Router Transitioner の setIsTransitioning が連続 dispatch →
  // React の setState 上限を超えて Minified React error #185 を引き起こす。
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // /login への redirect は同じ非認証フェーズ中に 1 回しか撃たない。pathname を
  // deps から外しても、auth re-fetch / TanStack Query reconciliation 等で
  // isAuthenticated / isLoading が transition 中に変動するシナリオを deps だけで
  // 網羅するのは不可能。連続 navigate は history.notify → router.load →
  // setIsTransitioning を同期で連発させ React の setState 上限を超えるため、
  // navigatedRef で「同 mount × 同非認証フェーズ中は最大 1 回」に固定する
  // （docs/web/product-innovation-lab.md「1 回ガード ref」規則）。
  // __root は SPA 起動中ずっと mount されているため、auth 回復時にガードをリセット
  // して次回 auth 失敗時にも redirect が走るようにする。
  const navigatedRef = useRef(false);

  // 認証リダイレクト
  useEffect(() => {
    if (devMockAuth) return; // 開発モック auth 時は /login へ飛ばさない
    if (isAuthenticated) {
      navigatedRef.current = false;
      return;
    }
    if (navigatedRef.current) return;
    if (isLoginPage || isLoading) return;
    navigatedRef.current = true;
    void navigate({ to: '/login', search: { redirect: pathnameRef.current } });
  }, [devMockAuth, isAuthenticated, isLoading, isLoginPage, navigate]);

  // ログインページ
  if (isLoginPage) {
    return <Outlet />;
  }

  // ローディング中 / 未認証（リダイレクト中）。開発モック auth 時はスキップして shell を描画。
  if (!devMockAuth && (isLoading || !isAuthenticated)) {
    return <LoadingScreen />;
  }

  if (isBareLayoutPath(location.pathname)) {
    return <Outlet />;
  }

  return <AuthenticatedShell />;
}
